import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Tooltip } from "@mui/material";

function clamp(n, min, max) { return Math.max(min, Math.min(n, max)); }

const SIZE_MAP = {
  xs: { outer: 76, pad: 3, chipH: 18, chipFs: 10, titleFs: 11 },
  sm: { outer: 92, pad: 3, chipH: 20, chipFs: 11, titleFs: 12 },
  md: { outer: 96, pad: 3, chipH: 20, chipFs: 11, titleFs: 12 },
};

const PromoStoryRing = ({
  avatar,
  title,
  amount,
  token,
  category,
  due,
  startAt,
  boosted,
  onClick,
  size = "sm",
  avatarScale = 2,
}) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const { progress, dueMs } = useMemo(() => {
    const dueTime = new Date(due).getTime();
    const startTime = startAt ? new Date(startAt).getTime() : Date.now() - 24 * 3600 * 1000;
    const total = Math.max(1, dueTime - startTime);
    const elapsed = clamp(Date.now() - startTime, 0, total);
    return { progress: clamp(elapsed / total, 0, 1), dueMs: Math.max(0, dueTime - now) };
  }, [now, due, startAt]);

  // яркие базовые цвета (категория)
  const baseColor = useMemo(() => {
    switch (category) {
      case "BuyAndHold":       return "#2d74d7"; // blue
      case "VoteWithWallet":   return "#14F195"; // green
      case "StakingChallenge": return "#ff6bcb"; // pink
      default:                 return "#9aa3b2"; // neutral
    }
  }, [category]);

  // срочность перекрывает базовый цвет
  const ringColor = useMemo(() => {
    const ONE_H = 3600 * 1000;
    const ONE_D = 24 * ONE_H;
    if (dueMs <= ONE_H) return "#ff3b30";  // red
    if (dueMs <= ONE_D) return "#ffcc00";  // yellow
    return baseColor;
  }, [dueMs, baseColor]);

  const angle = Math.round(progress * 360);

  const remain = useMemo(() => {
    const s = Math.floor(dueMs / 1000);
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  }, [dueMs]);

  const S = SIZE_MAP[size] || SIZE_MAP.sm;

  return (
    <Box
      onClick={onClick}
      sx={{
        width: S.outer + 24,
        flexShrink: 0,
        cursor: "pointer",
        userSelect: "none",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        transition: "transform .2s ease",
        "&:hover": { transform: "translateY(-4px)" },
      }}
    >
      {/* внешний прогресс-ринг + белые волны (на dark) */}
      <Box
        sx={{
          position: "relative",
          width: S.outer,
          height: S.outer,
          borderRadius: "50%",
          padding: `${S.pad}px`,
          // более читаемая «дорожка» на тёмном
          background: `conic-gradient(${ringColor} ${angle}deg, rgba(255,255,255,0.10) 0)`,
          boxShadow: boosted
            ? "0 0 0 6px rgba(255,255,255,.06), 0 12px 28px rgba(20,241,149,.35)"
            : "0 8px 24px rgba(2,8,23,.45)",
          overflow: "visible",
          "@keyframes pulseWhite": {
            "0%":   { transform: "scale(0.92)", opacity: 0.65 },
            "70%":  { transform: "scale(1.36)", opacity: 0 },
            "100%": { opacity: 0 },
          },
        }}
      >
        {boosted && (
          <>
            <Box
              sx={{
                position: "absolute",
                inset: -4,
                borderRadius: "50%",
                border: "2px solid rgba(255,255,255,.65)",
                animation: "pulseWhite 2.4s ease-out infinite",
                pointerEvents: "none",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                inset: -1,
                borderRadius: "50%",
                border: "2px solid rgba(255,255,255,.35)",
                animation: "pulseWhite 2.4s ease-out infinite",
                animationDelay: "1.2s",
                pointerEvents: "none",
              }}
            />
          </>
        )}

         {/* внутренняя маска с аватаром */}
        <Box
          sx={{
            position: "absolute",
            inset: S.pad,
            borderRadius: "50%",
            background: "rgba(12,16,22,.92)",
            border: "2px solid rgba(255,255,255,.85)", // тонкий белый ободок
            overflow: "hidden",
          }}
        >
          {/* фон-картинка с масштабом — заполняет круг целиком */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundImage: `url(${avatar})`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              backgroundSize: "cover",
              transform: `scale(${avatarScale})`,
              // небольшой антиалиасинг:
              willChange: "transform",
            }}
          />
          {/* {amount != null && String(amount).trim() !== "" && (
  <Chip
    label={String(amount)}          // только цифры, без токена
    size="small"
    sx={{
      position: "absolute",
      bottom: 6,
      left: "50%",
      transform: "translateX(-50%)",
      height: S.chipH,
      bgcolor: "#ffffff",           // белый чип
      color: "#111111",             // чёрный текст
      border: "1px solid rgba(0,0,0,.12)",
      boxShadow: "0 2px 8px rgba(0,0,0,.15)",
      "& .MuiChip-label": {
        px: 1,
        fontSize: S.chipFs,
        fontWeight: 900,            // толще — сочнее на dark
        lineHeight: `${S.chipH}px`,
        color: "#111111",
      },
    }}
  />
)} */}

        </Box>
      </Box>

      {/* подписи — белые/светлые, чтобы не теряться */}
      <Tooltip title={title} arrow>
        <Typography
          variant="body2"
          sx={{
            mt: 1,
            textAlign: "center",
            fontFamily: "'Bruno Ace SC', sans-serif",
            color: "#ffffff",
            lineHeight: 1.1,
            fontSize: S.titleFs,
            fontWeight: 800,
            maxWidth: S.outer + 24,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </Typography>
      </Tooltip>

      <Typography variant="caption" sx={{ color: "#e5e7eb", mt: 0.25, fontWeight: 700 }}>
        {remain} left
      </Typography>
    </Box>
  );
};

export default React.memo(PromoStoryRing);